import Alert from '../../../common/components/Alert';
import axios from '../../../common/utils/interceptor';
import { Box, Button, Grid, Typography, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import clsx from 'clsx';
import { NavigateBefore } from '@mui/icons-material';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import updateTaskItem from '../../../taskList/utils/updateTaskItem';
import { apiUrls, routeMappings, WorkflowItemCode } from '../../../constants';
import {
  CategoryRemaining,
  Customizations,
  DoneValidation,
  RaterNominationDetails,
} from '../../interfaces/RaterNominationDetails';
import { DoneValidationMessages } from './DoneValidationMessages';
import { FullPageLoader } from '../../../common/components/FullPageLoader';
import { getRouteMfsTaskListPage, getServerDomain } from '../../utils/helper';
import { RaterList } from './RaterList';
import { RaterPageContainer } from './RaterPageContainer';
import { RaterStyles } from '../../styles/rater';
import { RatingQuestionStyles } from '../../styles/ratingQuestion';
import { MODE, RaterNominationPageMode, Task } from '../../interfaces/SurveyTaskTile';
import { TaskItem } from '../../../taskList/interfaces';
import { TaskItemStatus, taskListDefaultConfiguration } from '../../../taskList/constants';
import { updateRater } from '../../utils/rater';
import { UpdateRaterMode } from '../../constants/updateRaterMode';
import { useNavigate, useParams } from 'react-router';
import { useRaterNominationDetails } from '../../store/mfsStoreApi';
import { useTranslation } from 'react-i18next';
import { LayoutStyles as HideFooter } from '../../styles/common';
import { HIGHLIGHT_RATER_DELAY } from '../../constants/shl360';
import { GARouteMappings, GAPageViewMappings } from '../../../constants/gaMappings';
import { sendPageViewDetails } from '../../../common/utils/gaEvents';
import { getCookie } from '../../../common/utils/cookies';
import HelpOutline from '@mui/icons-material/HelpOutline';
import RaterNominationHelp from './RaterNominationHelp';
import { RaterField } from './SearchRaterForm';
import { useAppSelector } from '../../../store/hook';
import { headerOptionsProvider } from '../../../store/providers/headerOptionsProvider';
import { WCAGStrandardStyles } from '../../../common/styles/WCAGStandardStyles';

interface RaterNominationHelp {
  toolTip: string;
  heading: string;
  categories: {
    subHead: string;
    desc: string;
  }[];
}

export const RaterNominationPage = (): React.JSX.Element => {
  /** Page View GA method */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.SHL360_RATERLIST, GAPageViewMappings.SHL360_RATERLIST);
  }, []);

  const { classes } = RaterStyles();
  const { classes: clsRQ } = RatingQuestionStyles();
  const { classes: customClass } = HideFooter();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routeParams = useParams<{ ratingId?: string }>();

  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  const { newAddedRaterId, setRatingId, setRaterNominationDetails, setNewAddedRaterId, ratingId } =
    useRaterNominationDetails();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorKey, setErrorKey] = useState<string>('');
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [raterDetails, setRaterDetails] = useState<RaterNominationDetails>({
    introContent: '',
    languages: [],
    page: {
      total: 0,
      min: 0,
      max: 0,
      disableAddNewRater: false,
      raters: [],
      mode: '',
    },
  });
  const [customizations, setCustomizations] = useState<Customizations>({
    override: false,
    categories: [],
  });
  const [doneButtonDisabled, setDoneButtonDisabled] = useState<boolean>(true);
  const [addButtonDisabled, setAddButtonDisabled] = useState<boolean>(false);
  const [doneValidation, setDoneValidation] = useState<DoneValidation>({
    overAllRemaining: 0,
    categoriesRemaining: [],
  });
  const [helpData, setHelpData] = useState<RaterNominationHelp>({
    heading: '',
    toolTip: '',
    categories: [{ subHead: '', desc: '' }],
  });
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [, setSearchFields] = useState<RaterField[]>();
  const [helpEnabled, setHelpEnabled] = useState<boolean>(false);
  const [checkRnFirstPage, setCheckRnFirstPage] = useState<boolean>(false);
  const { headerOptions } = useAppSelector((state) => state.headerOptions);
  const { setHeaderOptions } = headerOptionsProvider();
  const { classes: WCAGClasses } = WCAGStrandardStyles();
  // update mfs store with rating id
  useEffect(() => {
    if (routeParams && routeParams.ratingId) {
      setRatingId(routeParams.ratingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams?.ratingId]);

  const fetchRaterNominationHelp = async (): Promise<void> => {
    try {
      const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
      const { data } = await axios.get(`${getServerDomain(apiUrls.GET_RATER_HELP_TEXT)}`, config);
      // const { data } = await axios.get(apiUrls.GET_RATER_HELP_TEXT);
      if (data && data.content) {
        setHelpData(JSON.parse(data.content));
        setHelpEnabled(true);
      }
    } catch (e) {
      console.error('Failed fetching Help Texts!');
    }
  };

  const fetchRaterSearchFields = async (): Promise<void> => {
    try {
      setLoading(true);
      const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
      const { data } = await axios.get(getServerDomain(apiUrls.GET_MFS_RATER_SEARCH_FIELDS), config);
      setSearchFields(data);
      sessionStorage.setItem('searchFields', JSON.stringify(data));
    } catch (e) {
      setErrorKey('internalServerError');
    }
  };

  const fetchRaterNominationDetails = async (): Promise<void> => {
    try {
      setLoading(true);
      const { data } = await axios.post(`${apiUrls.MFS_RATER_NOMINATION_DETAILS_WITH_FEEDBACK_STATUS}`, {
        ratingId: routeParams.ratingId,
      });
      // const { data } = await axios.get(apiUrls.POST_MFS_RATER_NOMINATION_DETAILS);

      setRaterDetails(data);
      // update mfs store with rater nomination details
      setRaterNominationDetails(data);
      fetchRaterSearchFields();
    } catch (e) {
      setErrorKey('internalServerError');
    } finally {
      setLoading(false);
      fetchRaterNominationHelp();
    }
  };

  // Get customizations for validation of added raters in category
  const fetchCustomizations = async (): Promise<void> => {
    try {
      setLoading(true);
      const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
      const { data } = await axios.get(
        `${getServerDomain(apiUrls.GET_RATER_CUSTOMIZATIONS)}${routeParams.ratingId}`,
        config,
      );
      // const { data } = await axios.get(apiUrls.GET_RATER_CUSTOMIZATIONS);
      if (data && data.rating) {
        setCustomizations(data.rating);
      }
    } catch (e) {
      console.error('Failed fetching customizations!');
    } finally {
      fetchRaterNominationDetails();
    }
  };

  // for highlighting a newly added rater
  const fadeHighlightNewAddedRater = (): void => {
    if (newAddedRaterId !== '') {
      setTimeout(() => {
        setNewAddedRaterId('');
      }, HIGHLIGHT_RATER_DELAY);
    }
  };

  useEffect(() => {
    fetchCustomizations();
    fadeHighlightNewAddedRater();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update the rater nomination task status as In Progress
    const updateRaterNominationStatus = async (taskItem: TaskItem): Promise<void> => {
      try {
        const { status } = await axios.get(apiUrls.GET_RATER_NOMINATION_UPDATE_STATUS);
        if (status === 200) {
          updateTaskItem(taskItem, TaskItemStatus.IN_PROGRESS);
        }
      } catch (e) {
        setErrorKey('internalServerError');
      }
    };

    if (raterDetails.page.total > 0) {
      // Get status of task item RN from currentWorkflowItem
      const { configuration } = currentWorkflowItem;
      const parsedConfig =
        configuration && currentWorkflowItem.code === WorkflowItemCode.MFSTASKLIST
          ? JSON.parse(configuration).configWidgetJSON
          : taskListDefaultConfiguration;

      const tasks: Array<Task> = (parsedConfig && parsedConfig.tasks) || [];

      const rnTask = tasks
        .map((task: Task) => task.items)
        .flat()
        .find((item: TaskItem) => item.id === ratingId && item.code === WorkflowItemCode.RN);

      if (rnTask && rnTask.status === TaskItemStatus.NOT_STARTED) {
        updateRaterNominationStatus({ ...rnTask, taskId: +rnTask.id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raterDetails.page.total]);

  useEffect(() => {
    // Done button gets enabled only when min raters are added in each category
    const evalDoneDisabled = (): void => {
      const countExceedsMin = raterDetails?.page.raters.some((rater) => rater.count > 0);
      if (countExceedsMin !== undefined) {
        setDoneButtonDisabled(!countExceedsMin);
      }
      setCheckRnFirstPage(document?.URL.includes(routeMappings.RATERS));
    };

    // If total added raters in any category equals maximum possible count disable add buttons
    const evalAddDisabled = (): void => {
      setAddButtonDisabled(raterDetails?.page.total === raterDetails?.page.max);
    };

    const mapCustomizations = (): void => {
      raterDetails?.page.raters.forEach((rater) => {
        const filteredCustomization =
          customizations.categories &&
          customizations.categories.length > 0 &&
          customizations.categories.filter((custom) => custom.roleId.toString() === rater.roleId.toString());
        if (filteredCustomization && filteredCustomization.length > 0) {
          rater.required = filteredCustomization[0].required || undefined;
        }
      });
    };

    evalDoneDisabled();
    evalAddDisabled();
    if (customizations.override && customizations.categories && customizations.categories.length > 0) {
      mapCustomizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raterDetails]);

  /** Click handler for Add Rater button */
  const onAddClick = (roleId: number) => (): void => {
    const route = `${routeMappings.MFS}${routeMappings.SEARCH_RATER}/${roleId}`;
    navigate(route);
  };

  /** Api call to Remove Rater */
  const removeRater = async (roleId: number, raterId: number, ratingId: number): Promise<void> => {
    const responseErrorKey = await updateRater(UpdateRaterMode.DELETE, undefined, roleId.toString(), ratingId, raterId);
    setErrorKey(responseErrorKey);

    // Refetch the rater nomination data on success
    if (responseErrorKey === '') {
      fetchRaterNominationDetails();
    }
  };

  /** validate added rater count satisfying minimum count for each category */
  const validateRaters = (): boolean => {
    const { min, total, raters } = raterDetails.page;
    let isValid = true;
    let overAllRemaining = 0;
    const categoriesRemaining: Array<CategoryRemaining> = [];

    if (total < min) {
      overAllRemaining = min - total;
      isValid = false;
    }

    for (const rater of raters) {
      let customValidation = false;
      if (rater.required) {
        customValidation = rater.count > 0 && rater.count < rater.required;
      }

      if (customValidation) {
        categoriesRemaining.push({
          roleName: rater.roleName,
          remainingCount: rater.required ? rater.required - rater.count : rater.min - rater.count,
        });
        isValid = false;
      } else if (rater.count < rater.min) {
        categoriesRemaining.push({
          roleName: rater.roleName,
          remainingCount: rater.min - rater.count,
        });
        isValid = false;
      }
    }
    setDoneValidation({
      overAllRemaining,
      categoriesRemaining,
    });
    return isValid;
  };

  /** Click handler for Remove Rater button */
  const onRemoveClick = (roleId: number, raterId: number, ratingId: number) => (): void => {
    removeRater(roleId, raterId, ratingId);
  };

  /** Function to navigate to tasklist page */
  const redirectToMfsTaskListPage = (survey: boolean): void => {
    navigate(getRouteMfsTaskListPage(currentWorkflowItem), { state: { page: 'rnpage', submitted: survey } });
    setHeaderOptions({ ...headerOptions, ...{ isAssessment: false } });
  };

  /** Click handler for Back button */
  const onBackClick = (): void => {
    redirectToMfsTaskListPage(false);
  };

  /** End rater Nomination task as done */
  const markRaterNominationDone = async (): Promise<void> => {
    try {
      const { status } = await axios.post(apiUrls.POST_MFS_RATER_NOMINATION_END, {});
      if (status === 200) {
        redirectToMfsTaskListPage(true);
      }
    } catch (e) {
      setErrorKey('internalServerError');
    }
  };

  /** Click handler for Done button */
  const onDoneClick = (): void => {
    const isValid = validateRaters();
    if (isValid) {
      markRaterNominationDone();
      setAddButtonDisabled(true);
    } else {
      setErrorKey('doneValidationFail');
    }
  };

  useLayoutEffect(() => {
    const root = document.getElementById('root');
    root && (root.className = `noExitonHeader`);

    return (): void => {
      root && (root.className = '');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <FullPageLoader isLoading={loading} customClass={customClass.screenContainer} />
      ) : (
        <RaterPageContainer helpOpen={helpOpen}>
          {helpOpen && helpData ? (
            <RaterNominationHelp {...helpData} close={(): void => setHelpOpen(false)} />
          ) : (
            <>
              <Box textAlign="center" className={classes.fullWidth}>
                <Grid xs={12} item>
                  <Box className={`${classes.inlineFlex} ${classes.fullWidth}`}>
                    <Typography className={`${classes.pageTitle} ${classes.margin5} ${WCAGClasses.h2}`} component="h2">
                      {t('askForFeedback')}
                    </Typography>
                    {smDown ? (
                      <Button
                        aria-label={t('help_label')}
                        className={classes.helpButton}
                        disabled={!helpEnabled}
                        onClick={(): void => setHelpOpen(true)}>
                        <HelpOutline className={classes.helpIcon} />
                      </Button>
                    ) : (
                      <Tooltip title={helpData.toolTip ? helpData.toolTip : ''}>
                        <Button
                          aria-label={t('help_label')}
                          className={classes.helpButton}
                          disabled={!helpEnabled}
                          onClick={(): void => setHelpOpen(true)}>
                          {t('help_label')} <HelpOutline className={classes.helpIcon} />
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid xs={12} item>
                  <Typography className={`${classes.pageSubTitle} ${WCAGClasses.h3}`}>
                    {`${t('totalPeopleAdded')} (${raterDetails?.page.total})`}
                  </Typography>
                </Grid>
                {raterDetails?.page?.mode?.toLowerCase() !== MODE.VIEW.toLowerCase() &&
                  (raterDetails?.page.max !== null || raterDetails?.page.min !== null) && (
                    <Grid xs={12} item>
                      <Typography
                        variant="body1"
                        className={classes.pageSubTitle2}
                        component="h4"
                        aria-label={`${t('overall')}: ${raterDetails?.page.min} ${t('minimum')}, ${
                          raterDetails?.page.max
                        } ${t('maximum')}`}>
                        {`${t('overall')}: ${raterDetails?.page.min} ${t('minimum')}, ${raterDetails?.page.max} ${t(
                          'maximum',
                        )}`}
                      </Typography>
                    </Grid>
                  )}
              </Box>
              {/** Rater List */}
              <Grid xs={12} item>
                <RaterList
                  raterDetails={raterDetails}
                  newAddedRaterId={newAddedRaterId}
                  addButtonDisabled={addButtonDisabled}
                  onAddClick={onAddClick}
                  onRemoveClick={onRemoveClick}
                  mode={raterDetails?.page?.mode as RaterNominationPageMode}
                />
              </Grid>
              {raterDetails?.page?.mode?.toLowerCase() !== MODE.VIEW.toLowerCase() ? (
                errorKey === '' && raterDetails?.page?.mode?.toLowerCase() !== MODE.VIEW.toLowerCase() ? (
                  <Alert show={!doneButtonDisabled} state="success" gridClassName={classes.raterNominationNote}>
                    <Typography variant="body1" id="doneInfo">
                      {t('raterNominatioNote')}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert
                    show={true}
                    state="error"
                    gridClassName={errorKey === 'internalServerError' ? classes.errorMsgCenter : ''}>
                    {errorKey !== 'doneValidationFail' ? (
                      t(errorKey)
                    ) : (
                      <DoneValidationMessages validation={doneValidation} />
                    )}
                  </Alert>
                )
              ) : null}
              {/** Actions Button - Done and Back */}
              <Grid xs={12} item>
                <Box display="flex" mt={2} mb={6}>
                  {raterDetails?.page?.mode?.toLowerCase() !== MODE.VIEW.toLowerCase() ||
                    (!checkRnFirstPage && (
                      <Grid xs item className={classes.textAlignLeft}>
                        <Button
                          variant="contained"
                          className={clsx(classes.doneButton, classes.width100)}
                          onClick={onBackClick}
                          aria-label={t('back')}>
                          <NavigateBefore className={clsRQ.backArrowIcon} /> {t('back')}
                        </Button>
                      </Grid>
                    ))}
                  {raterDetails?.page?.mode?.toLowerCase() !== MODE.VIEW.toLowerCase() && (
                    <Grid xs item className={classes.textAlignRight}>
                      <Button
                        variant="contained"
                        className={clsx(classes.doneButton, classes.width100)}
                        disabled={doneButtonDisabled}
                        onClick={onDoneClick}
                        aria-label={t('done')}
                        aria-describedby="doneInfo">
                        <div>{t('done')}</div>
                      </Button>
                    </Grid>
                  )}
                </Box>
              </Grid>
            </>
          )}
        </RaterPageContainer>
      )}
    </>
  );
};
